/**
 * Radar's phone home screen, assembled (docs/PLAN-2026-09-21-mobile-os-shell.md §3, the Radar table). The components
 * are the ones ported from the desk (./mobile); this file fills them for the signed-in staff member:
 *
 * - System bar: "GoGamo Radar" and the role.
 * - Home: the apps this person may see, by their `primary` place, then More.
 * - Greeting and widgets: numbers from the reads the windows already make, under their query keys: the waiting
 *   reports, the schools (apps/engine/phone.tsx), today's learners.
 * - Dock: Home, Search, Profile.
 * - Profile: avatar (My profile), language, sign out. Radar has no way "back" to anything, so there is no exit row.
 *
 * Rendered only on a phone, so the desktop makes none of these reads.
 */
import type { ReactNode } from "react";
import { appById, ENGINE_GROUPS, type RadarAppDefinition } from "../apps/registry";
import { activeTodayLine, flagsStatLine, flagSearchText, flagsWidgetLine, schoolsNeedingLook, schoolsWidgetLine, useEnginePhoneData } from "../apps/engine/phone";
import { useAuth } from "../auth";
import { ADMIN_LANGUAGE_NAMES, useAppLabel, useI18n, type AdminLocale } from "../lib/i18n";
import { canSee } from "../lib/permissions";
import { AvatarArt, usePersonalize } from "../lib/personalize";
import { matches } from "./mobile/layout";
import { MobileHome, openOnPhone } from "./mobile/MobileHome";
import type { MobileProfileItem, MobileSearcher, MobileWidget, QuickAction, QuickActionContext, RadarMobile } from "./mobile/types";
import { ago, phoneApps, roleLabel } from "./phone-model";
import type { WindowState } from "./store";

export interface RadarPhoneProps {
  /** The apps this person may see, as the desktop's dock has them. */
  apps: RadarAppDefinition[];
  windows: Record<string, WindowState>;
  stack: string[];
  /** Anything a host (or a test) wants to set over the assembly. */
  mobile?: Partial<RadarMobile> | undefined;
}

export function RadarPhone({ apps, windows, stack, mobile: override }: RadarPhoneProps) {
  const { user, signOut, enginePermissions: permissions } = useAuth();
  const { avatar } = usePersonalize();
  const { locale, setLocale } = useI18n();
  const appLabel = useAppLabel();
  const role = user?.role;
  const may = (p: string) => canSee(p, role, permissions);

  // ---- the reads, each the one its window makes ----
  const eng = useEnginePhoneData({
    flags: may("engine.flags.review"),
    schools: may("engine.orgs.manage"),
    stats: !may("engine.flags.review") && may("engine.costs.view"),
  });
  const openApp = (id: string, params?: Record<string, string>) => {
    const def = apps.find((a) => a.id === id) ?? (id === "profile" ? appById("profile") : undefined);
    if (def) openOnPhone(def, params);
  };
  const iconOf = (id: string): ReactNode => appById(id)?.icon;

  // ---- numbers ----
  const waiting = eng.flags ? eng.flags.length : null;
  const needLook = eng.schools ? schoolsNeedingLook(eng.schools) : null;

  // ---- the home screen ----
  const stat: RadarMobile["stat"] =
    waiting !== null
      ? { line: flagsStatLine(waiting, eng.flagsFull), onOpen: () => openApp("engine-flags") }
      : eng.activeToday !== null
        ? { line: activeTodayLine(eng.activeToday), onOpen: () => openApp("engine-learners") }
        : null;

  const widgets: MobileWidget[] = [];
  if (eng.flags) widgets.push({ id: "flags", icon: iconOf("engine-flags"), title: "Reports waiting", line: flagsWidgetLine(eng.flags.length, eng.flagsFull), tone: eng.flags.length > 0 ? "accent" : "default", onOpen: () => openApp("engine-flags") });
  if (eng.schools) widgets.push({ id: "schools", icon: iconOf("engine-schools"), title: "Schools", line: schoolsWidgetLine(eng.schools), tone: needLook ? "accent" : "default", onOpen: () => openApp("engine-schools") });

  // ---- quick actions: only what the windows can already do, only for someone allowed to ----
  const qa: Record<string, (ctx: QuickActionContext) => QuickAction[]> = {
    "engine-staff": ({ launch }) => [{ id: "new", label: "New account", run: () => launch({ new: "1" }) }],
    "engine-flags": ({ launch }) => {
      const newest = eng.flags?.[0];
      return newest ? [{ id: "newest", label: "Newest waiting", run: () => launch({ id: newest.id }) }] : [];
    },
    "engine-schools": ({ launch }) => [{ id: "new", label: "New school", run: () => launch({ new: "1" }) }],
  };
  const badges: Record<string, number> = {};
  if (waiting !== null) badges["engine-flags"] = waiting;
  if (needLook !== null) badges["engine-schools"] = needLook;
  const phone = phoneApps(apps, { label: appLabel, quickActions: qa, badges });

  // ---- search: the apps, then what is already on hand ----
  const searchers: MobileSearcher[] = [];
  if (eng.flags?.length) {
    const flags = eng.flags;
    searchers.push({ id: "flags", label: "Reports", search: (q) => flags.filter((f) => matches(flagSearchText(f), q)).map((f) => ({ id: f.id, title: f.item?.stem?.slice(0, 80) || f.note || "A report", subtitle: [f.analytics_id, ago(f.at)].filter(Boolean).join(", "), icon: iconOf("engine-flags"), run: () => openApp("engine-flags", { id: f.id }) })) });
  }
  if (eng.schools?.length) {
    const schools = eng.schools;
    searchers.push({ id: "schools", label: "Schools", search: (q) => schools.filter((s) => matches(s.name, q)).map((s) => ({ id: s.id, title: s.name, icon: iconOf("engine-schools"), run: () => openApp("engine-schools", { id: s.id }) })) });
  }

  // ---- profile ----
  const profileItems: MobileProfileItem[] = [
    { id: "profile", label: appLabel("profile", "My profile"), detail: "Avatar and password", icon: iconOf("profile"), run: () => openApp("profile") },
    ...(Object.keys(ADMIN_LANGUAGE_NAMES) as AdminLocale[])
      .filter((l) => l !== locale)
      .map((l) => ({ id: `lang-${l}`, label: ADMIN_LANGUAGE_NAMES[l], detail: `Language, now ${ADMIN_LANGUAGE_NAMES[locale]}`, run: () => setLocale(l) })),
  ];

  const roleText = roleLabel(role);
  const mobile: RadarMobile = {
    subtitle: roleText,
    role: roleText,
    stat,
    widgets,
    searchers,
    profileItems,
    categories: [...ENGINE_GROUPS],
    ...(avatar ? { avatar: <AvatarArt id={avatar} size={40} /> } : {}),
    ...override,
  };

  return (
    <MobileHome
      apps={phone}
      windows={windows}
      stack={stack}
      title="GoGamo Radar"
      user={{ name: user?.displayName || user?.email || "", ...(user?.email ? { email: user.email } : {}) }}
      onSignOut={() => void signOut()}
      menuItems={[]}
      mobile={mobile}
    />
  );
}
